import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useTranslation } from 'react-i18next';
import { hasuraClient } from '../utils/graphqlClient';
import { useAuth } from './useReduxAuth';
import { USER_ROLES } from '../constants/permissions';
import {
  GET_DASHBOARD_STATS_QUERY,
  GET_DASHBOARD_JOBS_OVERVIEW_QUERY,
  GET_FIRM_IDS_BY_TEAM_QUERY,
  GET_FIRM_IDS_BY_USER_QUERY,
} from '../graphql';
import {
  GET_TEAMS_BY_COMPANY_QUERY,
  GET_USERS_BY_TEAM_QUERY,
} from '../graphql/queries/teams';

const STATUS_COLOR_MAP = [
  { keywords: ['onayl'], color: 'success' },
  { keywords: ['yazılım', 'geliştirme', 'devam', 'yazım'], color: 'info' },
  { keywords: ['değerlend', 'inceleme', 'beklem'], color: 'warning' },
  { keywords: ['tamamlan', 'bitti'], color: 'success' },
  { keywords: ['iptal', 'red', 'olumsuz', 'sonland'], color: 'error' },
];

const STATUS_LABELS = {
  YAZIMDA: 'Yazımda',
  DEGERLENDIRMEDE: 'Değerlendirmede',
  ONAYLANDI: 'Onaylandı',
  REDDEDILDI: 'Reddedildi',
  KAPATILDI: 'Kapatıldı',
  TAMAMLANDI: 'Tamamlandı',
  DEVRALINDI: 'Devralındı',
  ORTAK_BULUNDU: 'Ortak Bulundu',
  DANISMANLIK_SONLANDIRILDI: 'Danışmanlık Sonlandırıldı',
};

// Dashboard'da her zaman gorunecek "aktif calisma" statuleri (gizli filtre
// yok). Kart sayisi TOPLAM 6'ya cikarildi (istekle uyumlu): 4 aktif her zaman
// gorunur, kalan 2 slot mevcut jobsOverview'deki count'a gore EN CALABALIK 2
// statuden secilir. Bosluk kalirsa daha az kart cikar.
const ALWAYS_VISIBLE_STATUS_KEYS = new Set([
  'YAZIMDA',
  'DEGERLENDIRMEDE',
  'ONAYLANDI',
  'DEVRALINDI',
]);
// Herkesin gordugu temel kart sayisi. Yetkili roller bunun USTUNE iki
// ekstra bolum goruyor (bkz. PRIVILEGED_STATUS_KEYS).
const BASE_STATUS_CARDS = 4;

// Yalnizca ROOT ve DIREKTOR'e gosterilen statuler. "Tamamlandi" ve
// "Danismanlik Sonlandirildi" bitmis/dusmus isler; ekiplerin gunluk
// panosunda yer kaplamalari yerine yonetici gorunumunde kaliyorlar.
//
// Bunlar temel 4 kartin USTUNE ekleniyor; yetkisiz kullanici yalnizca
// temel 4'u goruyor.
const PRIVILEGED_STATUS_KEYS = new Set(['TAMAMLANDI', 'DANISMANLIK_SONLANDIRILDI']);
const PRIVILEGED_STATUS_ROLES = new Set([USER_ROLES.ROOT, USER_ROLES.DIRECTOR]);

const JOB_TYPE_LABELS = {
  AR_GE: 'AR-GE Merkezi',
  TASARIM_MERKEZI: 'Tasarım Merkezi',
  TEKNOPARK: 'Teknopark',
  TUBITAK: 'TÜBİTAK',
  ULUSLARARASI: 'Uluslararası',
};

const getStatusColor = statusName => {
  const lower = (statusName || '').toLowerCase();
  for (const { keywords, color } of STATUS_COLOR_MAP) {
    if (keywords.some(k => lower.includes(k))) return color;
  }
  return 'primary';
};

export const useDashboardData = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const role = user?.role;
  const userId = user?.id;
  const companyId = user?.companyId;
  const userTeamId = user?.team_id;

  // Role capabilities
  const showTeamFilter   = role === 'ROOT' || role === 'DIRECTOR';
  const showPersonFilter = role === 'ROOT' || role === 'DIRECTOR' || role === 'GROUP_MANAGER' || role === 'TEAM_MANAGER';
  const defaultNoFilter  = role === 'ROOT' || role === 'DIRECTOR'; // sees all company data by default
  const showFirmsStatForRole = role === 'ROOT' || role === 'DIRECTOR';

  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);

  const [stats, setStats] = useState({ firms: 0, contracts: 0, offers: 0, tasks: 0 });
  const [jobsOverview, setJobsOverview] = useState([]);
  const [loading, setLoading] = useState(true);

  const firmIdsRef = useRef(undefined);

  const resolveTeamFirmIds = useCallback(async teamId => {
    if (!teamId) return null;
    try {
      const data = await hasuraClient.query(GET_FIRM_IDS_BY_TEAM_QUERY, {
        teamId: parseInt(teamId),
      });
      return [...new Set((data.user_firms ?? []).map(uf => uf.firm_id))];
    } catch (err) {
      console.error('resolveTeamFirmIds error:', err);
      return [];
    }
  }, []);

  const resolveUserFirmIds = useCallback(async uid => {
    if (!uid) return null;
    try {
      const data = await hasuraClient.query(GET_FIRM_IDS_BY_USER_QUERY, {
        userId: parseInt(uid),
      });
      return (data.user_firms ?? []).map(uf => uf.firm_id);
    } catch (err) {
      console.error('resolveUserFirmIds error:', err);
      return [];
    }
  }, []);

  const fetchData = useCallback(async (firmIds, offerCreatorId) => {
    if (!userId) return;

    if (Array.isArray(firmIds) && firmIds.length === 0 && !offerCreatorId) {
      setStats({ firms: 0, contracts: 0, offers: 0, tasks: 0 });
      setJobsOverview([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      const threeMonthsAgoISO = threeMonthsAgo.toISOString();

      const companyFilter = companyId ? { company_id: { _eq: companyId } } : {};
      // null = no firm filter (full company); [] = impossible filter (returns 0)
      // so a person with zero assigned firms doesn't accidentally see all of
      // the company's contracts when we skip the early-return for offers.
      const firmFilter = !Array.isArray(firmIds)
        ? {}
        : firmIds.length > 0
          ? { firm_id: { _in: firmIds } }
          : { firm_id: { _in: [-1] } };
      // When the person filter selects a specific user, count offers by who
      // created them (created_by) rather than by firm authorization.
      const offerFilter = offerCreatorId
        ? { created_by: { _eq: offerCreatorId } }
        : firmFilter;

      const firmWhere         = { ...companyFilter };
      const contractWhere     = { ...companyFilter, ...firmFilter, created_at: { _gte: threeMonthsAgoISO } };
      const offerWhere        = { ...companyFilter, ...offerFilter, created_at: { _gte: threeMonthsAgoISO } };
      const taskWhere         = { ...companyFilter, task_status: { _neq: 'completed' } };
      const jobsOverviewWhere = { ...companyFilter, ...firmFilter };

      const [statsData, jobsData] = await Promise.all([
        hasuraClient.query(GET_DASHBOARD_STATS_QUERY, {
          firmWhere,
          contractWhere,
          offerWhere,
          taskWhere,
        }),
        hasuraClient.query(GET_DASHBOARD_JOBS_OVERVIEW_QUERY, {
          where: jobsOverviewWhere,
        }),
      ]);

      setStats({
        firms: Array.isArray(firmIds) ? firmIds.length : (statsData.firms?.length ?? 0),
        contracts: statsData.contracts?.length ?? 0,
        offers: statsData.offers_aggregate?.aggregate?.count ?? 0,
        tasks: statsData.tasks?.length ?? 0,
      });
      setJobsOverview(jobsData.jobs ?? []);
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, companyId]);

  // ROOT / DIRECTOR: load all teams for the team filter dropdown
  useEffect(() => {
    if (!showTeamFilter || !userId) return;
    hasuraClient.query(GET_TEAMS_BY_COMPANY_QUERY, {
      where: companyId ? { company_id: { _eq: companyId } } : {},
    }).then(data => setTeams(data.teams ?? [])).catch(() => {});
  }, [showTeamFilter, userId, companyId]);

  // GROUP_MANAGER / TEAM_MANAGER: load own team's users for the person filter dropdown
  useEffect(() => {
    if (!showPersonFilter || showTeamFilter || !userTeamId) return;
    hasuraClient.query(GET_USERS_BY_TEAM_QUERY, { teamId: parseInt(userTeamId) })
      .then(data => setTeamUsers(data.users ?? []))
      .catch(() => {});
  }, [showPersonFilter, showTeamFilter, userTeamId]);

  // Initial data load
  useEffect(() => {
    if (!userId) return;

    if (defaultNoFilter) {
      // ROOT / DIRECTOR: all company data
      firmIdsRef.current = null;
      fetchData(null);
    } else if (role === 'TEAM_MEMBER') {
      // TEAM_MEMBER: only their own assigned firms
      resolveUserFirmIds(userId).then(ids => {
        const resolved = ids ?? [];
        firmIdsRef.current = resolved;
        fetchData(resolved);
      });
    } else if (userTeamId) {
      // TEAM_MANAGER / GROUP_MANAGER with a team: aggregate team members' firms
      resolveTeamFirmIds(userTeamId).then(ids => {
        const resolved = ids ?? [];
        firmIdsRef.current = resolved;
        fetchData(resolved);
      });
    } else {
      // GROUP_MANAGER without a team_id: show all company data
      firmIdsRef.current = null;
      fetchData(null);
    }
  }, [userId, role, defaultNoFilter, userTeamId, fetchData, resolveTeamFirmIds, resolveUserFirmIds]);

  // ROOT / DIRECTOR: team dropdown change
  const handleTeamChange = useCallback(async rawTeamId => {
    const teamId = rawTeamId ? parseInt(rawTeamId) : null;
    setSelectedTeamId(teamId);
    setSelectedUserId(null);
    setTeamUsers([]);

    let ids;
    if (teamId) {
      setLoading(true);
      try {
        const data = await hasuraClient.query(GET_USERS_BY_TEAM_QUERY, { teamId });
        setTeamUsers(data.users ?? []);
      } catch (err) {
        console.error('fetchTeamUsers error:', err);
      }
      ids = await resolveTeamFirmIds(teamId);
    } else {
      ids = null;
    }

    firmIdsRef.current = ids;
    fetchData(ids);
  }, [resolveTeamFirmIds, fetchData]);

  // Person dropdown change (all roles that have it)
  const handleUserChange = useCallback(async rawUserId => {
    const uid = rawUserId ? parseInt(rawUserId) : null;
    setSelectedUserId(uid);

    let ids;
    if (uid) {
      // Specific user selected → show only their assigned firms
      ids = await resolveUserFirmIds(uid);
    } else {
      // Deselected → fall back to team view
      const backTeamId = selectedTeamId || (defaultNoFilter ? null : userTeamId);
      ids = backTeamId ? await resolveTeamFirmIds(backTeamId) : (defaultNoFilter ? null : []);
    }

    firmIdsRef.current = ids;
    // Offers count for the selected person comes from offers they created
    // (created_by), independent of firm authorization. Other metrics still
    // use firm-based filtering.
    fetchData(ids, uid);
  }, [selectedTeamId, userTeamId, defaultNoFilter, resolveTeamFirmIds, resolveUserFirmIds, fetchData]);

  const projectStatusGroups = useMemo(() => {
    // 1) Tum statuleri sayarak grupla — hicbir sey haric tutulmuyor (top-N
    //    kismindaki filtre karari asagida veriliyor).
    const groups = {};
    jobsOverview.forEach(job => {
      const statusKey = job.job_status;
      // FK relation onceligi, yoksa legacy text alani.
      const typeKey = job.job_type_rel?.name || job.job_type;
      const statusName = (statusKey && STATUS_LABELS[statusKey]) || statusKey || 'Diğer';
      const subjectName = (typeKey && JOB_TYPE_LABELS[typeKey]) || typeKey || 'Diğer';
      if (!groups[statusName]) {
        groups[statusName] = { statusKey, subjects: {}, total: 0 };
      }
      groups[statusName].subjects[subjectName] = (groups[statusName].subjects[subjectName] || 0) + 1;
      groups[statusName].total++;
    });

    // 2) Kart secimi:
    //    - TEMEL 4 kart herkese: once "aktif calisma" statuleri, bos kalan
    //      yer olursa en kalabalik diger statulerle tamamlanir.
    //    - EKSTRA 2 bolum ("Tamamlandi", "Danismanlik Sonlandirildi")
    //      yalnizca ROOT/DIREKTOR'e ve temel 4'un USTUNE eklenir.
    const entries = Object.entries(groups).map(([statusName, data]) => ({
      statusName,
      statusKey: data.statusKey,
      color: getStatusColor(statusName),
      subjects: Object.entries(data.subjects)
        .sort(([, a], [, b]) => b - a)
        .map(([name, count]) => ({ name, count })),
      total: data.total,
    }));

    // Bitmis is statuleri ("Tamamlandi", "Danismanlik Sonlandirildi")
    // yalnizca ROOT/DIREKTOR'e gosteriliyor.
    const privileged = PRIVILEGED_STATUS_ROLES.has(role);

    // Temel kartlar ayricalikli statuleri ICERMEZ; onlar ayri ekleniyor.
    const baseEntries = entries.filter(e => !PRIVILEGED_STATUS_KEYS.has(e.statusKey));

    const active = baseEntries
      .filter(e => e.statusKey && ALWAYS_VISIBLE_STATUS_KEYS.has(e.statusKey))
      .sort((a, b) => b.total - a.total);

    // "Aktif calisma" statulerinden bazilarinda hic is olmayabilir; o zaman
    // o kart hic olusmaz. Kalan yerleri en kalabalik diger statulerle
    // dolduruyoruz ki temel kart sayisi 4'te kalsin — ornegin "Devralindi"
    // bos oldugunda yerine "Reddedildi" geliyor.
    const fillers = baseEntries
      .filter(e => !e.statusKey || !ALWAYS_VISIBLE_STATUS_KEYS.has(e.statusKey))
      .sort((a, b) => b.total - a.total)
      .slice(0, Math.max(0, BASE_STATUS_CARDS - active.length));

    // EKSTRA bolumler: "Tamamlandi" ve "Danismanlik Sonlandirildi".
    // Yalnizca ROOT/DIREKTOR goruyor ve temel 4'un yerini ALMIYORLAR.
    const extras = privileged
      ? entries
        .filter(e => PRIVILEGED_STATUS_KEYS.has(e.statusKey))
        .sort((a, b) => b.total - a.total)
      : [];

    // Aktif olanlar once, sonra dolgular, en sonda yetkiye bagli ekstralar.
    return [...active, ...fillers, ...extras];
  }, [jobsOverview, role]);

  const showFirmsCount = showFirmsStatForRole;

  const quickStats = useMemo(() => {
    const base = [
      {
        title: t('dashboard.quickStats.contracts'),
        value: stats.contracts,
        icon: DescriptionIcon,
        color: 'success',
      },
      {
        title: t('dashboard.quickStats.offers'),
        value: stats.offers,
        icon: HandshakeIcon,
        color: 'warning',
      },
      {
        title: t('dashboard.quickStats.tasks'),
        value: stats.tasks,
        icon: AssignmentIcon,
        color: 'error',
      },
    ];

    if (showFirmsCount) {
      return [
        {
          title: t('dashboard.quickStats.firms'),
          value: stats.firms,
          icon: BusinessIcon,
          color: 'primary',
        },
        ...base,
      ];
    }
    return base;
  }, [t, stats, showFirmsCount]);

  return {
    loading,
    quickStats,
    projectStatusGroups,
    showTeamFilter,
    showPersonFilter,
    teams,
    teamUsers,
    selectedTeamId,
    selectedUserId,
    onTeamChange: handleTeamChange,
    onUserChange: handleUserChange,
  };
};
